import type { UserInfo } from 'matomo-tracker-react-native';
import MatomoTracker, { MatomoProvider, useMatomo } from 'matomo-tracker-react-native';
import React, { createContext, useContext, useState } from 'react';

export type AnalyticsEventParams = {
  action: string;
  name?: string;
  category?: string;
  value?: number;
  campaign?: string;
  userInfo?: UserInfo;
};

export type AnalyticsPageViewParams = {
  name: string;
  url?: string;
  userInfo?: UserInfo;
};

export type AnalyticsContextType = {
  isInitialized: boolean;
  isEnabled: boolean;
  userId?: string;
  presetUserInfo?: UserInfo;
  actions: {
    setEnabled: (enabled: boolean) => void;
    setPresetUserInfo: (userInfo: UserInfo) => void;
    clearPresetUserInfo: () => void;
    trackAppStart: (userInfo?: UserInfo) => Promise<void>;
    trackEvent: (params: AnalyticsEventParams) => Promise<void>;
    trackScreenView: (params: AnalyticsPageViewParams) => Promise<void>;
    trackAction: (params: AnalyticsPageViewParams) => Promise<void>;
    trackSiteSearch: (params: {
      keyword: string;
      category?: string;
      count?: number;
      userInfo?: UserInfo;
    }) => Promise<void>;
    trackLink: (params: { link: string; userInfo?: UserInfo }) => Promise<void>;
    trackDownload: (params: { download: string; userInfo?: UserInfo }) => Promise<void>;
    setUserId: (userId: string | null) => void;
    setCustomDimension: (id: number, value: string) => void;
    reset: () => void;
  };
};

export const AnalyticsContext = createContext<AnalyticsContextType>({
  isInitialized: false,
  isEnabled: true,
  actions: {
    setEnabled: () => {},
    setPresetUserInfo: () => {},
    clearPresetUserInfo: () => {},
    trackAppStart: async () => {},
    trackEvent: async () => {},
    trackScreenView: async () => {},
    trackAction: async () => {},
    trackSiteSearch: async () => {},
    trackLink: async () => {},
    trackDownload: async () => {},
    setUserId: () => {},
    setCustomDimension: () => {},
    reset: () => {},
  },
});

export function useAnalyticsContext() {
  return useContext(AnalyticsContext);
}

// Inner component that has access to Matomo
function AnalyticsInner({
  children,
  userId,
  enableLogging,
}: {
  children: React.ReactNode;
  userId?: string;
  enableLogging?: boolean;
}) {
  const { trackEvent, trackScreenView, trackAppStart, trackAction, trackSiteSearch, trackLink, trackDownload } =
    useMatomo();
  const [isEnabled, setIsEnabled] = useState(true);
  const [userIdState, setUserIdState] = useState<string | undefined>(userId);
  const [presetUserInfo, setPresetUserInfoState] = useState<UserInfo | undefined>(undefined);
  const [log] = useState(enableLogging || false);

  const handleSetEnabled = (enabled: boolean) => {
    setIsEnabled(enabled);
  };

  const handleSetPresetUserInfo = (userInfo: UserInfo) => {
    setPresetUserInfoState(userInfo);
  };

  const handleClearPresetUserInfo = () => {
    setPresetUserInfoState(undefined);
  };

  const handleTrackAppStart = async (userInfo?: UserInfo) => {
    if (!userIdState || !isEnabled) {
      if (log) {
        console.debug('Analytics: Track App Start skipped - not initialized or disabled');
      }
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...presetUserInfo, ...userInfo };

      await trackAppStart({
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (log) {
        console.debug('Analytics: App start tracked successfully', { userInfo: mergedUserInfo });
      }
    } catch (error) {
      console.error('Analytics: Failed to track app start', error);
    }
  };

  const handleTrackEvent = async (params: AnalyticsEventParams) => {
    if (!userIdState || !isEnabled) {
      if (log) {
        console.debug('Analytics: Track Event skipped - not initialized or disabled', params);
      }
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...presetUserInfo, ...params.userInfo };

      await trackEvent({
        category: params.category || 'User Action',
        action: params.action,
        name: params.name,
        value: params.value,
        campaign: params.campaign,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (log) {
        console.debug('Analytics: Event tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track event', error, params);
    }
  };

  const handleTrackScreenView = async (params: AnalyticsPageViewParams) => {
    if (!userIdState || !isEnabled) {
      if (log) {
        console.debug('Analytics: Track Screen View skipped - not initialized or disabled', params);
      }
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...presetUserInfo, ...params.userInfo };

      await trackScreenView({
        name: params.name,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (log) {
        console.debug('Analytics: Screen view tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track screen view', error, params);
    }
  };

  const handleTrackAction = async (params: AnalyticsPageViewParams) => {
    if (!userIdState || !isEnabled) {
      if (log) {
        console.debug('Analytics: Track Action skipped - not initialized or disabled', params);
      }
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...presetUserInfo, ...params.userInfo };

      await trackAction({
        name: params.name,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (log) {
        console.debug('Analytics: Action tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track action', error, params);
    }
  };

  const handleTrackSiteSearch = async (params: {
    keyword: string;
    category?: string;
    count?: number;
    userInfo?: UserInfo;
  }) => {
    if (!userIdState || !isEnabled) {
      if (log) {
        console.debug('Analytics: Track Site Search skipped - not initialized or disabled', params);
      }
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...presetUserInfo, ...params.userInfo };

      await trackSiteSearch({
        keyword: params.keyword,
        category: params.category,
        count: params.count,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (log) {
        console.debug('Analytics: Site search tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track site search', error, params);
    }
  };

  const handleTrackLink = async (params: { link: string; userInfo?: UserInfo }) => {
    if (!userIdState || !isEnabled) {
      if (log) {
        console.debug('Analytics: Track Link skipped - not initialized or disabled', params);
      }
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...presetUserInfo, ...params.userInfo };

      await trackLink({
        link: params.link,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (log) {
        console.debug('Analytics: Link tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track link', error, params);
    }
  };

  const handleTrackDownload = async (params: { download: string; userInfo?: UserInfo }) => {
    if (!userIdState || !isEnabled) {
      if (log) {
        console.debug('Analytics: Track Download skipped - not initialized or disabled', params);
      }
      return;
    }

    try {
      // Merge preset userInfo with provided userInfo
      const mergedUserInfo = { ...presetUserInfo, ...params.userInfo };

      await trackDownload({
        download: params.download,
        userInfo: Object.keys(mergedUserInfo).length > 0 ? mergedUserInfo : undefined,
      });

      if (log) {
        console.debug('Analytics: Download tracked successfully', params);
      }
    } catch (error) {
      console.error('Analytics: Failed to track download', error, params);
    }
  };

  const handleSetUserId = (userId: string | null) => {
    setUserIdState(userId || undefined);

    // Update preset userInfo with the new userId
    if (userId) {
      setPresetUserInfoState((prev) => ({
        ...prev,
        uid: userId,
      }));
    } else {
      // Remove uid from preset userInfo if userId is null
      setPresetUserInfoState((prev) => {
        if (prev?.uid) {
          const updatedUserInfo = { ...prev };
          delete updatedUserInfo.uid;
          return Object.keys(updatedUserInfo).length > 0 ? updatedUserInfo : undefined;
        }
        return prev;
      });
    }

    if (log) {
      console.debug('Analytics: User ID updated in preset userInfo', userId);
    }
  };

  const handleSetCustomDimension = (id: number, value: string) => {
    if (!presetUserInfo) {
      setPresetUserInfoState({ [`dimension${id}`]: value });
    } else {
      setPresetUserInfoState({
        ...presetUserInfo,
        [`dimension${id}`]: value,
      });
    }

    if (log) {
      console.debug('Analytics: Custom dimension set in preset userInfo', { id, value });
    }
  };

  const reset = () => {
    setUserIdState(undefined);
    setPresetUserInfoState(undefined);
    setIsEnabled(true);

    console.debug('Analytics: Provider reset successfully');
  };

  const contextValue: AnalyticsContextType = {
    isInitialized: true, // Always true since initialization is handled by MatomoProvider
    isEnabled,
    userId: userIdState,
    presetUserInfo,
    actions: {
      setEnabled: handleSetEnabled,
      setPresetUserInfo: handleSetPresetUserInfo,
      clearPresetUserInfo: handleClearPresetUserInfo,
      trackAppStart: handleTrackAppStart,
      trackEvent: handleTrackEvent,
      trackScreenView: handleTrackScreenView,
      trackAction: handleTrackAction,
      trackSiteSearch: handleTrackSiteSearch,
      trackLink: handleTrackLink,
      trackDownload: handleTrackDownload,
      setUserId: handleSetUserId,
      setCustomDimension: handleSetCustomDimension,
      reset,
    },
  };

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
}

export type AnalyticsProviderProps = {
  children: React.ReactNode;
  disabled?: boolean;
  instance?: MatomoTracker;
  userId?: string;
  enableLogging?: boolean;
};

export function AnalyticsProvider({
  children,
  disabled = false,
  instance,
  userId,
  enableLogging = false,
}: AnalyticsProviderProps) {
  // If analytics is disabled, provide a no-op context
  if (disabled) {
    const noOpContextValue: AnalyticsContextType = {
      isInitialized: false,
      isEnabled: false,
      actions: {
        setEnabled: () => {},
        setPresetUserInfo: () => {},
        clearPresetUserInfo: () => {},
        trackAppStart: async () => {},
        trackEvent: async () => {},
        trackScreenView: async () => {},
        trackAction: async () => {},
        trackSiteSearch: async () => {},
        trackLink: async () => {},
        trackDownload: async () => {},
        setUserId: () => {},
        setCustomDimension: () => {},
        reset: () => {},
      },
    };

    return <AnalyticsContext.Provider value={noOpContextValue}>{children}</AnalyticsContext.Provider>;
  }

  // Use provided instance or create a default placeholder
  const matomoInstance =
    instance ||
    new MatomoTracker({
      urlBase: 'https://placeholder.com',
      siteId: 1,
      disabled: true, // Disabled until properly initialized
    });

  return (
    <MatomoProvider instance={matomoInstance}>
      <AnalyticsInner userId={userId} enableLogging={enableLogging}>
        {children}
      </AnalyticsInner>
    </MatomoProvider>
  );
}
